import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import NavigationHeader from '../../components/NavigationHeader';
import SectionScrollIndicator from '../../components/SectionScrollIndicator';
import SkillCategory from './components/SkillCategory';
import SkillFilter from './components/SkillFilter';
import FeaturedSkills from './components/FeaturedSkills';
import SkillsStats from './components/SkillsStats';
import SkillTimeline from './components/SkillTimeline';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const SkillsTechnicalExpertise = () => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProficiency, setSelectedProficiency] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filteredSkills, setFilteredSkills] = useState({});
  const navigate = useNavigate();
  // Mock data for skills organized by categories
  const skillsData = {
    'Frontend Technologies': [
      {
        name: 'React',
        proficiency: 'Advanced',
        experience: '2+ years',
        icon: 'Atom',
        description: 'Building scalable web applications with modern React patterns, hooks, and context API.',
        certifications: ['React Developer Certification', 'Advanced React Patterns']
      },
      {
        name: 'TypeScript',
        proficiency: 'Advanced',
        experience: '1+ years',
        icon: 'Code2',
        description: 'Type-safe JavaScript development with advanced TypeScript features and patterns.',
        certifications: ['TypeScript Fundamentals']
      },
      {
        name: 'Next.js',
        proficiency: 'Advanced',
        experience: '2+ years',
        icon: 'Zap',
        description: 'Full-stack React framework for production-ready applications with SSR and SSG.',
        certifications: []
      },
      {
        name: 'Vue.js',
        proficiency: 'Advanced',
        experience: '1.6+ years',
        icon: 'Triangle',
        description: 'Progressive JavaScript framework for building user interfaces and SPAs.',
        certifications: []
      },
      {
        name: 'Tailwind CSS',
        proficiency: 'Advanced',
        experience: '2+ years',
        icon: 'Palette',
        description: 'Utility-first CSS framework for rapid UI development and consistent design systems.',
        certifications: []
      },
      {
        name: 'SASS/SCSS',
        proficiency: 'Advanced',
        experience: '4.5+ years',
        icon: 'Brush',
        description: 'CSS preprocessor for maintainable and scalable stylesheet architecture.',
        certifications: []
      }
    ],
    'Backend Development': [
      {
        name: 'Node.js',
        proficiency: 'Expert',
        experience: '4.5+ years',
        icon: 'Server',
        description: 'Server-side JavaScript runtime for building scalable network applications.',
        certifications: ['Node.js Application Developer']
      },
      {
        name: 'Express.js',
        proficiency: 'Expert',
        experience: '4.5+ years',
        icon: 'Zap',
        description: 'Fast, unopinionated web framework for Node.js applications and APIs.',
        certifications: []
      },
      {
        name: 'GraphQL',
        proficiency: 'Advanced',
        experience: '4.5+ years',
        icon: 'Share2',
        description: 'Query language and runtime for APIs with efficient data fetching.',
        certifications: []
      },
      {
        name: 'PHP',
        proficiency: 'Advanced',
        experience: '2+ years',
        icon: 'Code2',
        description: 'Server-side scripting language for web development and backend services.',
        certifications: []
      },
      {
        name: 'EJS',
        proficiency: 'Expert',
        experience: '4.5+ years',
        icon: 'FileCode',
        description: 'Embedded JavaScript templating engine for server-side rendering.',
        certifications: []
      },
      {
        name: 'Passport.js',
        proficiency: 'Expert',
        experience: '2+ years',
        icon: 'Shield',
        description: 'Authentication middleware for Node.js applications.',
        certifications: []
      },
      {
        name: 'ORM',
        proficiency: 'Advanced',
        experience: '4.5+ years',
        icon: 'Database',
        description: 'Object-Relational Mapping for database operations and data management.',
        certifications: []
      },
      {
        name: 'RBAC',
        proficiency: 'Advanced',
        experience: '2+ years',
        icon: 'Lock',
        description: 'Role-Based Access Control for secure application authorization.',
        certifications: []
      },
      {
        name: 'REST APIs',
        proficiency: 'Expert',
        experience: '4.5+ years',
        icon: 'Link',
        description: 'Designing and implementing RESTful web services and API architectures.',
        certifications: []
      }
    ],
    'Databases': [
      {
        name: 'MongoDB',
        proficiency: 'Expert',
        experience: '4.5+ years',
        icon: 'Database',
        description: 'NoSQL document database for flexible and scalable data storage.',
        certifications: ['MongoDB Developer Associate']
      },
      {
        name: 'Mongoose',
        proficiency: 'Expert',
        experience: '4.5+ years',
        icon: 'FileCode',
        description: 'MongoDB object modeling tool for Node.js applications.',
        certifications: []
      },
      {
        name: 'MySQL',
        proficiency: 'Advanced',
        experience: '4.5+ years',
        icon: 'Database',
        description: 'Popular relational database management system for web applications.',
        certifications: []
      },
      {
        name: 'Redis',
        proficiency: 'Intermediate',
        experience: '2+ years',
        icon: 'Zap',
        description: 'In-memory data structure store for caching and session management.',
        certifications: []
      }
    ],
    'DevOps & Tools': [
      {
        name: 'Docker',
        proficiency: 'Intermediate',
        experience: '4.5+ years',
        icon: 'Package',
        description: 'Containerization platform for consistent development and deployment environments.',
        certifications: ['Docker Certified Associate']
      },
      {
        name: 'Git/GitHub',
        proficiency: 'Expert',
        experience: '4.5+ years',
        icon: 'GitBranch',
        description: 'Distributed version control system for tracking code changes and collaboration.',
        certifications: []
      },
      {
        name: 'Cloud Basics',
        proficiency: 'Intermediate',
        experience: '2+ years',
        icon: 'Cloud',
        description: 'Fundamental understanding of cloud computing concepts and services.',
        certifications: []
      },
      {
        name: 'CI/CD',
        proficiency: 'Intermediate',
        experience: '2+ years',
        icon: 'Zap',
        description: 'Continuous Integration and Continuous Deployment practices.',
        certifications: []
      },
    ],
    'CMS & Others': [
      {
        name: 'WordPress',
        proficiency: 'Advanced',
        experience: '2+ years',
        icon: 'Globe',
        description: 'Content management system with custom plugin and theme development.',
        certifications: []
      },
      {
        name: 'CRM',
        proficiency: 'Advanced',
        experience: '2+ years',
        icon: 'Users',
        description: 'Customer Relationship Management systems and implementation.',
        certifications: []
      }
    ],
    'Soft Skills': [
      {
        name: 'Team Leadership',
        proficiency: 'Advanced',
        experience: '2+ years',
        icon: 'Users',
        description: 'Leading development teams and mentoring junior developers.',
        certifications: ['Agile Leadership Certificate']
      },
      {
        name: 'Project Management',
        proficiency: 'Advanced',
        experience: '2+ years',
        icon: 'Calendar',
        description: 'Managing software projects from conception to delivery using Agile methodologies.',
        certifications: ['Scrum Master Certification']
      },
      {
        name: 'Problem Solving',
        proficiency: 'Expert',
        experience: '4.5+ years',
        icon: 'Lightbulb',
        description: 'Analytical thinking and creative problem-solving for complex technical challenges.',
        certifications: []
      },
      {
        name: 'Communication',
        proficiency: 'Advanced',
        experience: '4.5+ years',
        icon: 'MessageCircle',
        description: 'Effective technical communication with stakeholders and cross-functional teams.',
        certifications: []
      },
      {
        name: 'Mentoring',
        proficiency: 'Advanced',
        experience: '2+ years',
        icon: 'GraduationCap',
        description: 'Guiding and developing junior developers and interns.',
        certifications: []
      }
    ]
  };

  // Featured skills data
  const featuredSkills = [
    {
      name: 'React',
      proficiency: 'Advanced',
      experience: '2+ years',
      description: 'Building scalable, performant web applications with modern React patterns, hooks, and state management solutions.',
      keyProjects: ['E-commerce Platform', 'Dashboard Analytics', 'Social Media App'],
      certifications: ['React Developer Certification', 'Advanced React Patterns']
    },
    {
      name: 'Node.js',
      proficiency: 'Advanced',
      experience: '4.5+ years',
      description: 'Developing robust backend services, APIs, and microservices architecture with Node.js ecosystem.',
      keyProjects: ['API Gateway', 'Real-time Chat System', 'Payment Processing'],
      certifications: ['Node.js Application Developer']
    },
    {
      name: 'TypeScript',
      proficiency: 'Advanced',
      experience: '1+ years',
      description: 'Type-safe development with advanced TypeScript features, generics, and architectural patterns.',
      keyProjects: ['Enterprise CRM', 'Design System', 'CLI Tools'],
      certifications: ['TypeScript Fundamentals']
    },
    {
      name: 'Cloud Basics',
      proficiency: 'Intermediate',
      experience: '2+ years',
      description: 'Fundamental understanding of cloud computing concepts and services.',
      keyProjects: ['Serverless API', 'Auto-scaling Web App', 'Data Pipeline'],
      certifications: []
    },
    {
      name: 'Docker',
      proficiency: 'Intermediate',
      experience: '4.5+ years',
      description: 'Containerization strategies, multi-stage builds, and orchestration for consistent deployments.',
      keyProjects: ['Microservices Platform', 'CI/CD Pipeline', 'Development Environment'],
      certifications: ['Docker Certified Associate']
    },
    {
      name: 'MongoDB',
      proficiency: 'Advanced',
      experience: '4.5+ years',
      description: 'NoSQL database design, aggregation pipelines, and performance optimization for scalable applications.',
      keyProjects: ['Content Management System', 'Analytics Platform', 'User Management'],
      certifications: ['MongoDB Developer Associate']
    }
  ];

  // Skills statistics
  const skillsStats = [
    { label: 'Total Technologies', value: '10+', growth: 5 },
    { label: 'Years Experience', value: '4.5+', growth: null },
    { label: 'Certifications', value: '10+', growth: 25 },
    { label: 'Projects Completed', value: '30+', growth: 20 }
  ];

  // Timeline data
  const timelineData = [
    {
      year: "2025",
      duration: "Ongoing",
      title: "Full-Stack SaaS & OTT Platform Development",
      description: "Currently focused on building scalable, modular, and AI-enhanced SaaS architecture with modern frontend experiences. Working on creating intelligent, high-performance systems that seamlessly blend backend efficiency with user-centric design.",
      technologies: [ "Next.js", "React","TailwindCSS", "TypeScript", "Node.js", "Express.js", "GraphQL", "Apollo Server", "MongoDB", "Redis", "BullMQ", "Docker", "Cloudinary", "Cloudflare Stream", "AI Tools", "ChatGPT", "GitHub Copilot","TensorFlow.js", "OpenAI API", "Passport.js" ],
      achievement: "Leading the architecture and early-stage development of a highly scalable, modular SaaS platform integrating AI-based features and media delivery systems. Also, engineering a unified full-stack environment leveraging AI-assisted development to optimize productivity, scalability, and performance across frontend and backend systems.",
      icon: "Layers3",
      status: "current"
    },    
    {
      year: '2024',
      duration: 'Ongoing',
      title: 'AI Integration & Productivity Tools',
      description: 'Currently exploring AI tools and technologies to improve development productivity and reduce development time through intelligent automation.',
      technologies: ['AI Tools', 'ChatGPT', 'GitHub Copilot', 'AI Code Generation', 'Productivity Automation'],
      achievement: 'Exploring various AI tools to streamline development workflow and enhance code quality',
      icon: 'Cpu',
      status: 'current'
    },
    {
      year: '2023',
      duration: 'Ongoing',
      title: 'Senior MERN Stack Developer & Technical Consultant at Cosmic Info Solution',
      description: 'Serving as the primary technical point of contact, designing scalable architectures on the MERN stack, and advising clients on digital transformation strategies.',
      technologies: ['JavaScript', 'React', 'Node.js', 'Express.js', 'MongoDB', 'AWS', 'REST APIs', 'System Architecture'],
      achievement: 'Successfully managed full project lifecycles from architecture to deployment for various client engagements.',
      icon: 'Briefcase',
      status: 'current'
    },
    {
      year: '2021-2023',
      duration: '2 years',
      title: 'Lead MERN Stack Developer at Iqonic Design',
      description: 'Started as an Intern and progressed to leading the ground-up MERN Stack implementation of KiviCare and other SaaS products.',
      technologies: ['React', 'Node.js', 'Express.js', 'MongoDB', 'Mongoose', 'REST APIs', 'WordPress'],
      achievement: 'Core developer and lead for KiviCare, WP Bookit, SocialV, and Streamit used by global clients. Successfully delivered the complete MERN implementation of KiviCare.',
      projects: [
        { name: 'KiviCare', url: 'https://demo.kivicare.io/' },
        { name: 'WP Bookit', url: 'https://demo-wpbookit.iqonic.design/' },
        { name: 'SocialV', url: 'https://socialv-wordpress.iqonic.design/' },
        { name: 'Streamit', url: 'https://streamit.iqonic.design/' }
      ],
      icon: 'Briefcase'
    },
    {
      year: '2022',
      duration: '6 months',
      title: 'NSDC Backend Development Certification',
      description: 'Comprehensive training program covering full-stack development from basic HTML to advanced Node.js backend development.',
      technologies: ['HTML/CSS', 'JavaScript', 'Node.js', 'Express.js', 'MongoDB', 'REST APIs'],
      achievement: 'Completed comprehensive backend development certification covering entire web development stack',
      icon: 'GraduationCap'
    },
    {
      year: '2019-2022',
      duration: '3 years',
      title: 'Bachelor of Commerce (BCOM)',
      description: 'Academic foundation in business principles, commerce, and analytical thinking that complements technical skills.',
      technologies: ['Business Mathematics', 'Financial Accounting', 'Business Economics', 'Marketing Management'],
      achievement: 'Developed strong analytical and problem-solving skills with 7.5/10 GPA',
      icon: 'BookOpen'
    },
    {
      year: '2019',
      duration: 'Self-taught',
      title: 'Frontend Development Foundation',
      description: 'Self-directed learning in modern web development, starting with HTML, CSS, and JavaScript fundamentals.',
      technologies: ['HTML5', 'CSS3', 'JavaScript ES6+', 'Responsive Design'],
      achievement: 'Built first portfolio projects and personal websites',
      icon: 'Monitor'
    }
  ];

  // Initialize expanded categories
  useEffect(() => {
    const initialExpanded = {};
    Object.keys(skillsData).forEach(category => {
      initialExpanded[category] = false;
    });
    setExpandedCategories(initialExpanded);
    setFilteredSkills(skillsData);
  }, []);

  // Filter skills based on search and filters
  useEffect(() => {
    let filtered = {};
    
    Object.keys(skillsData).forEach(category => {
      if (selectedCategory && selectedCategory !== category) {
        return;
      }

      const categorySkills = skillsData[category].filter(skill => {
        const matchesSearch = !searchTerm || 
          skill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          skill.description.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesProficiency = !selectedProficiency || 
          skill.proficiency === selectedProficiency;

        return matchesSearch && matchesProficiency;
      });

      if (categorySkills.length > 0) {
        filtered[category] = categorySkills;
      }
    });

    setFilteredSkills(filtered);
  }, [searchTerm, selectedProficiency, selectedCategory]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProficiency('');
    setSelectedCategory('');
  };

  const categories = Object.keys(skillsData);
  const hasResults = Object.keys(filteredSkills).length > 0;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Skills & Technical Expertise - Dishant Madariya | Full Stack Developer</title>
        <meta
          name="description"
          content="Explore the complete technical skill set of Full Stack Developer Dishant Madariya including React, Node.js, TypeScript, databases, DevOps and cloud technologies."
        />
        <meta
          name="keywords"
          content="Dishant Madariya skills, full stack developer skills, React, Node.js, TypeScript, AWS, Docker, MongoDB, technical expertise"
        />
        <link rel="canonical" href="https://port-folio-three-teal.vercel.app/skills" />
      </Helmet>

      <SectionScrollIndicator />

      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-6">
                <Icon name="Code" size={32} color="white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
                Skills & Technical Expertise
              </h1>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
                A comprehensive overview of my technical proficiencies, development expertise, and continuous learning journey in modern software development.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => window.open('/assets/Dishant_Madariya_Resume.pdf', '_blank')}
                >
                  Download Resume
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="ExternalLink"
                  iconPosition="right" 
                  onClick={() => window.open('https://github.com/DishantMadariya', '_blank')}
                >
                  View GitHub
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Skills Statistics */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SkillsStats stats={skillsStats} />
          </div>
        </section>

        {/* Featured Skills */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <FeaturedSkills featuredSkills={featuredSkills} />
          </div>
        </section>

        {/* Skills Filter and Categories */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-text-primary mb-4">Complete Technology Stack</h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Explore my comprehensive skill set across frontend, backend, databases, DevOps, and soft skills.
              </p>
            </div>

            <SkillFilter
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              selectedProficiency={selectedProficiency}
              onProficiencyChange={setSelectedProficiency}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              onClearFilters={clearFilters}
              categories={categories}
            />

            {hasResults ? (
              <div className="space-y-6">
                {Object.keys(filteredSkills).map(category => (
                  <SkillCategory
                    key={category}
                    category={category}
                    skills={filteredSkills[category]}
                    isExpanded={expandedCategories[category]}
                    onToggle={() => toggleCategory(category)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-6">
                  <Icon name="Search" size={32} className="text-text-secondary" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary mb-2">No skills found</h3>
                <p className="text-text-secondary mb-6">
                  Try adjusting your search criteria or clearing the filters.
                </p>
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Learning Timeline */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SkillTimeline timelineData={timelineData} />
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-primary dark:to-secondary rounded-2xl p-8">
              <h2 className="text-3xl font-bold mb-4 text-slate-900 dark:text-white">Ready to Collaborate?</h2>
              <p className="text-xl mb-8 text-slate-700 dark:text-white/90">
                Let's discuss how my technical expertise can contribute to your next project.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="secondary"
                  size="lg"
                  iconName="Mail"
                  iconPosition="left"
                  onClick={() => navigate('/contact')}
                  className="bg-primary text-white hover:bg-primary/90 dark:bg-secondary dark:hover:bg-secondary/80"
                >
                  Get In Touch
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="FolderOpen"
                  iconPosition="right"
                  onClick={() => navigate('/projects')}
                  className="border-2 border-primary text-primary hover:bg-primary hover:text-white dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-primary"
                >
                  View Projects
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SkillsTechnicalExpertise;